import React, { useState, useEffect } from "react";
import { fetchProgramStats } from "../../../../../utils/axios";
import ChartComponent from "./ChartComponent";
import ChartSelector from "./ChartSelector";
import { AlertCircle, Download, RefreshCw } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const CustomerProgram = () => {
  const [chartType, setChartType] = useState("radar");
  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await fetchProgramStats();
      setProgramData(data);
      setError(null);
    } catch (err) {
      setError("Failed to load program statistics. Please try again later.");
      console.error("Error loading program stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleExport = async (format = "pdf") => {
    const chartElement = document.querySelector(".chart-export-target");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL("image/png");

    if (format === "pdf") {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("program_chart.pdf");
    } else if (format === "jpg") {
      const link = document.createElement("a");
      link.href = imgData;
      link.download = "program_chart.jpg";
      link.click();
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white rounded shadow p-4"
        style={{ height: "16rem" }}
      >
        <div
          className="spinner-border text-primary"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        ></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="mr-10">
          <ChartSelector
            activeChart={chartType}
            onChartTypeChange={handleChartTypeChange}
          />
        </div>
        <div className="ms-auto d-flex gap-2">
          <button
            onClick={handleRefresh}
            className="btn border-0"
            title="Refresh Data"
          >
            <RefreshCw size={20} />
          </button>
          <div className="dropdown">
            <button
              className="btn border-0 dropdown-toggle"
              type="button"
              id="exportDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Export Chart"
            >
              <Download size={20} />
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end"
              aria-labelledby="exportDropdown"
            >
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => handleExport("pdf")}
                >
                  Download as PDF
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => handleExport("jpg")}
                >
                  Download as Image
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="alert alert-danger border rounded mb-4 d-flex align-items-center"
          role="alert"
        >
          <AlertCircle className="me-2" size={20} />
          <div>
            {error}
            <p className="mb-0 small mt-1">
              Using sample data for demonstration.
            </p>
          </div>
        </div>
      )}
      <div className="mt-4 rounded p-3 chart-export-target">
        <ChartComponent key={chartType} type={chartType} data={programData} />
      </div>
    </div>
  );
};

export default CustomerProgram;
